import { Link } from "react-router-dom";
import {
  ArrowRight,
  Mic,
  Video,
  Keyboard,
  MessageCircle,
  Brain,
  CheckCircle2,
  User,
  Calendar,
} from "lucide-react";

// ────────────────────────────────────────────────────────────────
//  Homepage · Desktop English (first pass)
//
//  Composed entirely from existing shared primitives (Header lives
//  in the page composer) + native React + Tailwind + JobClose
//  design tokens. The approved production hero background image
//  provides the visual foundation; all UI is live over it.
//
//  No animations, no Lottie, no motion library. Levi remains
//  static per PO launch decision.
//
//  Static marketing demo — the YOU/LEVI card is not wired to
//  Twilio, Resend, backend execution, or persistence.
// ────────────────────────────────────────────────────────────────

const COPY_HOME = {
  en: {
    kicker: "BUILT BY A CONTRACTOR, FOR CONTRACTORS.",
    h1_a: "Built for contractors.",
    h1_b_pre: "Powered by ",
    h1_b_hilite: "Levi AI.",
    h1_c: "Built to work the way you do.",

    control: "Your business. Your pricing. Your decisions.",

    p1:
      "Levi is your AI coworker inside JobClose AI. He understands your customers and jobs, remembers your work, and helps organize everything from the jobsite to close.",

    stop_lead: "And you don\u2019t have to stop working to use him.",
    p2:
      "Talk to Levi naturally, hands-free, just like you would a coworker. Tell him what you\u2019re seeing, what needs to get done, or show him the job with a video walkthrough. Levi captures the details and helps keep the work moving.",
    p3: "Prefer to type it in yourself? You can do that too.",

    talk_title_a: "Talk to Levi \u2014",
    talk_title_b: "Hands-Free",
    talk_body_a: "Keep working.",
    talk_body_b: "Just tell Levi what you need.",

    show_title: "Show Levi the Job",
    show_body_a: "Walk the job.",
    show_body_b: "Show Levi what you see.",

    cta_demo: "Schedule a Demo",
    cta_signin: "Sign In",

    micro_hands: "HANDS-FREE FIRST",
    micro_type: "TYPE IT YOUR WAY",

    // YOU / LEVI card
    you_label: "YOU",
    you_message:
      "\u201cLevi, text and email Manuel the material takeoff and work order for the Walter White roof job in Spanish.\u201d",
    levi_label: "LEVI",
    levi_message: "Done. I sent both documents to Manuel in Spanish.",
    text_sent: "Text sent",
    text_channel: "\u2022\u2022\u2022\u2022 3535",
    email_sent: "Email sent",
    email_channel: "m\u2022\u2022\u2022\u2022\u2022@example.com",

    // Bottom capability strip
    caps: [
      {
        icon: MessageCircle,
        title: "Talk naturally, hands-free",
        body: "Keep working while Levi captures the details.",
      },
      {
        icon: Video,
        title: "Show Levi the job",
        body: "Video walkthroughs give Levi eyes on the job.",
      },
      {
        icon: Brain,
        title: "Levi understands your business",
        body: "Customers, jobs, pricing, history \u2014 Levi remembers so you don\u2019t have to.",
      },
      {
        icon: CheckCircle2,
        title: "Levi gets it done",
        body: "Levi takes action across JobClose AI and confirms it\u2019s complete.",
      },
    ],
  },
};

// Spanish · natural contractor voice, "tú" throughout. JobClose AI
// and Levi preserved as brand names. Static marketing demonstration
// only. Same DOM feeds both languages — no layout change.
COPY_HOME.es = {
  kicker: "CREADO POR UN CONTRATISTA, PARA CONTRATISTAS.",
  h1_a: "Hecho para contratistas.",
  h1_b_pre: "Impulsado por ",
  h1_b_hilite: "Levi AI.",
  h1_c: "Hecho para trabajar como t\u00fa trabajas.",

  control: "Tu negocio. Tus precios. Tus decisiones.",

  p1:
    "Levi es tu compa\u00f1ero de trabajo con IA dentro de JobClose AI. Entiende a tus clientes y tus trabajos, recuerda tu historial y te ayuda a organizar todo desde la obra hasta el cierre del trabajo.",

  stop_lead: "Y no tienes que dejar de trabajar para usarlo.",
  p2:
    "Habla con Levi de forma natural y manos libres, igual que hablar\u00edas con un compa\u00f1ero de trabajo. Dile lo que est\u00e1s viendo, lo que necesitas hacer o mu\u00e9strale el trabajo con un recorrido en video. Levi captura los detalles y te ayuda a mantener el trabajo en marcha.",
  p3: "\u00bfPrefieres ingresar la informaci\u00f3n t\u00fa mismo? Tambi\u00e9n puedes hacerlo.",

  talk_title_a: "Habla con Levi \u2014",
  talk_title_b: "Manos libres",
  talk_body_a: "Sigue trabajando.",
  talk_body_b: "Solo dile a Levi lo que necesitas.",

  show_title: "Mu\u00e9strale el trabajo a Levi",
  show_body_a: "Recorre el trabajo.",
  show_body_b: "Mu\u00e9strale a Levi lo que ves.",

  cta_demo: "Agenda una Demo",
  cta_signin: "Iniciar sesi\u00f3n",

  micro_hands: "MANOS LIBRES PRIMERO",
  micro_type: "O ESCR\u00cdBELO A TU MANERA",

  // YOU / LEVI card
  you_label: "T\u00da",
  you_message:
    "\u201cLevi, env\u00edale por texto y correo electr\u00f3nico a Manuel la lista de materiales y la orden de trabajo del techo de Walter White en espa\u00f1ol.\u201d",
  levi_label: "LEVI",
  levi_message: "Listo. Le envi\u00e9 ambos documentos a Manuel en espa\u00f1ol.",
  text_sent: "Texto enviado",
  text_channel: "\u2022\u2022\u2022\u2022 3535",
  email_sent: "Correo enviado",
  email_channel: "m\u2022\u2022\u2022\u2022\u2022@example.com",

  // Bottom capability strip
  caps: [
    {
      icon: MessageCircle,
      title: "Habla con Levi, manos libres",
      body: "Sigue trabajando mientras Levi captura los detalles.",
    },
    {
      icon: Video,
      title: "Mu\u00e9strale el trabajo a Levi",
      body: "Los recorridos en video le permiten a Levi ver el trabajo.",
    },
    {
      icon: Brain,
      title: "Levi entiende tu negocio",
      body: "Clientes, trabajos, precios e historial \u2014 Levi recuerda para que t\u00fa no tengas que hacerlo.",
    },
    {
      icon: CheckCircle2,
      title: "Levi lo hace",
      body: "Levi realiza acciones dentro de JobClose AI y te confirma cuando est\u00e1n completas.",
    },
  ],
};

const HomeBody = ({ lang = "en" }) => {
  const c = COPY_HOME[lang] || COPY_HOME.en;
  return (
    <main className="bg-white" data-testid="home-body">
      {/* ─── HERO ─── Approved background photograph provides the
          visual foundation. All UI (headline, copy, CTA cluster,
          conversation card) lives live over it via parent grid. */}
      <section
        className="relative overflow-hidden"
        data-testid="home-hero"
      >
        {/* Approved production hero background — used directly,
            not regenerated, not cropped. Positioned so the
            contractor + Levi stay in the right-center as
            designed. */}
        <img
          src="/brand/home/hero_home_bg_v4.png"
          alt="A contractor kneels on a plywood subfloor inside a home under construction, holding his phone up to speak. Levi, a black Goldendoodle in a JobClose AI cap and blue collar, lies calmly beside him. A laser tape measure paints a red line across the floor toward building plans."
          className="hidden lg:block absolute inset-0 w-full h-full object-cover select-none"
          style={{ objectPosition: "center center" }}
          draggable={false}
          data-testid="home-hero-bg"
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:pl-6 lg:pr-10 pt-12 pb-16 lg:pt-16 lg:pb-24">
          <div className="grid grid-cols-12 gap-6 lg:gap-8 items-start">
            {/* ─── LEFT · headline, copy, cards, CTAs ─── */}
            <div className="col-span-12 lg:col-span-6">
              <div
                className="text-[13px] font-bold tracking-[0.18em] text-[var(--jc-blue)]"
                data-testid="home-kicker"
              >
                {c.kicker}
              </div>

              <h1
                className="mt-5 font-display font-extrabold text-[var(--jc-text)] leading-[1.02] tracking-tight text-[40px] lg:text-[52px] lg:max-w-[700px]"
                data-testid="home-headline"
              >
                <div>{c.h1_a}</div>
                <div>
                  {c.h1_b_pre}
                  <span className="text-[var(--jc-blue)]">{c.h1_b_hilite}</span>
                </div>
                <div>{c.h1_c}</div>
              </h1>

              <div className="mt-6 w-16 h-[3px] bg-[var(--jc-blue)] rounded-full" />

              <p
                className="mt-6 font-display font-extrabold text-[20px] lg:text-[22px] text-[var(--jc-text)] leading-snug lg:max-w-[440px]"
                data-testid="home-control"
              >
                {c.control}
              </p>

              <div className="mt-4 space-y-5 text-[16px] lg:text-[18px] leading-[1.7] text-[var(--jc-text)] font-medium lg:max-w-[440px]">
                <p>{c.p1}</p>
                <p className="font-display font-extrabold text-[18px] lg:text-[20px] leading-snug text-[var(--jc-blue)]">
                  {c.stop_lead}
                </p>
                <p>{c.p2}</p>
                <p>{c.p3}</p>
              </div>

              {/* ─── MOBILE-ONLY visual moment · contractor + Levi photo
                  (dedicated mobile asset, portrait crop) followed by the
                  compact YOU/LEVI conversation card. Hidden on lg+ where
                  these live in the desktop grid. */}
              <div className="lg:hidden mt-7 space-y-5" data-testid="home-mobile-visual">
                <div
                  className="relative rounded-2xl overflow-hidden ring-1 ring-black/10 shadow-[0_18px_40px_-16px_rgba(15,23,42,0.35)]"
                  data-testid="home-mobile-photo"
                >
                  <img
                    src="/brand/home/hero_home_mobile_v1.png"
                    alt="A contractor kneels on a concrete slab inside a home under construction, holding his phone up to speak. Beside him, Levi, a black Goldendoodle wearing a JobClose AI cap and a blue collar with a bone-shaped LEVI tag, lies calmly. A yellow tape measure lies extended between them and building plans rest on the floor to the right."
                    className="block w-full h-auto select-none"
                    draggable={false}
                  />
                </div>

                <div
                  className="rounded-2xl bg-white ring-1 ring-black/10 shadow-[0_18px_40px_-18px_rgba(15,23,42,0.35)] p-4"
                  data-testid="home-levi-card-mobile"
                >
                  {/* YOU */}
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--jc-blue)] text-white grid place-items-center">
                      <User size={20} strokeWidth={2.4} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11.5px] font-bold tracking-[0.16em] text-[var(--jc-blue)]">
                        {c.you_label}
                      </div>
                      <div className="mt-1 rounded-xl bg-[#EEF4FF] px-3 py-2.5 text-[14px] leading-[1.45] text-[var(--jc-text)]">
                        {c.you_message}
                      </div>
                    </div>
                  </div>

                  {/* LEVI */}
                  <div className="mt-3.5 flex items-start gap-3">
                    <img
                      src="/levi/levi-badge.webp"
                      alt=""
                      aria-hidden="true"
                      draggable={false}
                      className="w-10 h-10 rounded-full flex-shrink-0 select-none ring-1 ring-black/10"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11.5px] font-bold tracking-[0.16em] text-[var(--jc-blue)]">
                        {c.levi_label}
                      </div>
                      <div className="mt-1 rounded-xl bg-white ring-1 ring-black/10 px-3 py-2.5 text-[14px] leading-[1.45] text-[var(--jc-text)]">
                        {c.levi_message}
                      </div>
                    </div>
                  </div>

                  {/* Delivery confirmation rows */}
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2.5 text-[13px] text-[var(--jc-text)]" data-testid="home-delivery-text-mobile">
                      <CheckCircle2 size={18} strokeWidth={2.6} className="text-[var(--jc-green)] flex-shrink-0" />
                      <span className="font-semibold">{c.text_sent}</span>
                      <span className="text-[var(--jc-text-soft)]">·</span>
                      <span className="text-[var(--jc-text-soft)] font-mono tracking-wider">{c.text_channel}</span>
                    </div>
                    <div className="flex items-center gap-2.5 text-[13px] text-[var(--jc-text)]" data-testid="home-delivery-email-mobile">
                      <CheckCircle2 size={18} strokeWidth={2.6} className="text-[var(--jc-green)] flex-shrink-0" />
                      <span className="font-semibold">{c.email_sent}</span>
                      <span className="text-[var(--jc-text-soft)]">·</span>
                      <span className="text-[var(--jc-text-soft)] font-mono tracking-wider">{c.email_channel}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Two mode cards — Talk / Show */}
              <div
                className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:max-w-[500px]"
                data-testid="home-mode-cards"
              >
                <div
                  className="rounded-2xl bg-white/95 backdrop-blur-sm ring-1 ring-black/10 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.25)] p-5 flex items-start gap-4"
                  data-testid="home-card-talk"
                >
                  <span className="flex-shrink-0 w-12 h-12 rounded-xl bg-[var(--jc-blue)] text-white grid place-items-center">
                    <Mic size={24} strokeWidth={2.4} />
                  </span>
                  <div className="min-w-0">
                    <div className="font-display font-extrabold text-[17px] leading-tight text-[var(--jc-text)]">
                      {c.talk_title_a}
                      <br />
                      {c.talk_title_b}
                    </div>
                    <div className="mt-2 text-[14px] leading-snug text-[var(--jc-text-soft)]">
                      {c.talk_body_a}
                      <br />
                      {c.talk_body_b}
                    </div>
                  </div>
                </div>

                <div
                  className="rounded-2xl bg-white/95 backdrop-blur-sm ring-1 ring-black/10 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.25)] p-5 flex items-start gap-4"
                  data-testid="home-card-show"
                >
                  <span className="flex-shrink-0 w-12 h-12 rounded-xl bg-[var(--jc-blue)] text-white grid place-items-center">
                    <Video size={24} strokeWidth={2.4} />
                  </span>
                  <div className="min-w-0">
                    <div className="font-display font-extrabold text-[17px] leading-tight text-[var(--jc-text)]">
                      {c.show_title}
                    </div>
                    <div className="mt-2 text-[14px] leading-snug text-[var(--jc-text-soft)]">
                      {c.show_body_a}
                      <br />
                      {c.show_body_b}
                    </div>
                  </div>
                </div>
              </div>

              {/* Primary + secondary CTAs */}
              <div className="mt-7 flex items-center gap-3">
                <Link
                  to="/schedule-a-demo"
                  className="inline-flex items-center gap-2 rounded-md jc-btn-blue px-5 py-3 font-semibold shadow-lg text-white"
                  data-testid="home-cta-demo"
                >
                  <Calendar size={16} strokeWidth={2.4} />
                  {c.cta_demo}
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center rounded-md px-5 py-3 font-semibold text-[var(--jc-text)] bg-white/95 ring-1 ring-black/15 hover:bg-white"
                  data-testid="home-cta-signin"
                >
                  {c.cta_signin}
                </Link>
              </div>

              {/* Micro pill row */}
              <div className="mt-5 flex items-center gap-6 text-[12.5px] font-bold tracking-[0.14em]">
                <span className="inline-flex items-center gap-2 text-[var(--jc-blue)]" data-testid="home-micro-hands">
                  <Mic size={15} strokeWidth={2.6} />
                  {c.micro_hands}
                </span>
                <span className="inline-flex items-center gap-2 text-[var(--jc-text)]" data-testid="home-micro-type">
                  <Keyboard size={15} strokeWidth={2.6} />
                  {c.micro_type}
                </span>
              </div>
            </div>

            {/* ─── RIGHT · YOU / LEVI conversation card (desktop only) ─── */}
            <div className="hidden lg:block lg:col-span-6 lg:pl-6">
              <div
                className="rounded-2xl bg-white/95 backdrop-blur-sm ring-1 ring-black/10 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)] p-4 lg:p-5 lg:ml-auto lg:-mt-2 lg:max-w-[360px]"
                data-testid="home-levi-card"
              >
                {/* YOU */}
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--jc-blue)] text-white grid place-items-center">
                    <User size={20} strokeWidth={2.4} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11.5px] font-bold tracking-[0.16em] text-[var(--jc-blue)]">
                      {c.you_label}
                    </div>
                    <div className="mt-1 rounded-xl bg-[#EEF4FF] px-3 py-2.5 text-[14px] leading-[1.45] text-[var(--jc-text)]">
                      {c.you_message}
                    </div>
                  </div>
                </div>

                {/* LEVI */}
                <div className="mt-3.5 flex items-start gap-3">
                  <img
                    src="/levi/levi-badge.webp"
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="w-10 h-10 rounded-full flex-shrink-0 select-none ring-1 ring-black/10"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[11.5px] font-bold tracking-[0.16em] text-[var(--jc-blue)]">
                      {c.levi_label}
                    </div>
                    <div className="mt-1 rounded-xl bg-white ring-1 ring-black/10 px-3 py-2.5 text-[14px] leading-[1.45] text-[var(--jc-text)]">
                      {c.levi_message}
                    </div>
                  </div>
                </div>

                {/* Delivery confirmation rows */}
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2.5 text-[13px] text-[var(--jc-text)]" data-testid="home-delivery-text">
                    <CheckCircle2 size={18} strokeWidth={2.6} className="text-[var(--jc-green)] flex-shrink-0" />
                    <span className="font-semibold">{c.text_sent}</span>
                    <span className="text-[var(--jc-text-soft)]">·</span>
                    <span className="text-[var(--jc-text-soft)] font-mono tracking-wider">{c.text_channel}</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-[13px] text-[var(--jc-text)]" data-testid="home-delivery-email">
                    <CheckCircle2 size={18} strokeWidth={2.6} className="text-[var(--jc-green)] flex-shrink-0" />
                    <span className="font-semibold">{c.email_sent}</span>
                    <span className="text-[var(--jc-text-soft)]">·</span>
                    <span className="text-[var(--jc-text-soft)] font-mono tracking-wider">{c.email_channel}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── BOTTOM CAPABILITY STRIP · dark navy, 4 pills ─── */}
      <section
        className="bg-[var(--jc-navy)] text-white"
        data-testid="home-caps-strip"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {c.caps.map((cap, i) => {
            const Icon = cap.icon;
            return (
              <div key={i} className="flex items-start gap-4" data-testid={`home-cap-${i}`}>
                <span className="flex-shrink-0 w-11 h-11 rounded-full bg-[var(--jc-blue)] grid place-items-center">
                  <Icon size={22} strokeWidth={2.4} />
                </span>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-[17px] leading-tight">{cap.title}</div>
                  <div className="mt-2 text-[14px] leading-snug text-white/80">{cap.body}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default HomeBody;
